import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import type { SkillAccessibility } from "@/shared/domain/skills";
import {
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationLead,
  ConfirmationNote,
  ConfirmationPanel,
  ConfirmationTags,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";
import { coerceToSkillDraft, type CreateSkillInput } from "./createSkillCoerce";
import styles from "./CreateSkillConfirmation.module.css";

const VISIBILITY_LABELS: Record<SkillAccessibility, string> = {
  private: "Private",
  org: "Org",
  "org-editable": "Org-editable",
};

/**
 * Read-only preview of the draft the model wants to seed into the create
 * editor. Runs the proposal through the same {@link coerceToSkillDraft} the
 * tool's `execute` uses, so the name, body, deduped tags, and resolved
 * visibility shown here match exactly what the editor opens with.
 *
 * The editor — not approval — is the validation seam now, so an incomplete
 * proposal still previews what it can (and the omitted fields simply don't
 * render); the user fills the rest in the editor. The only "nothing to show"
 * case is a proposal with no name, description, *and* body, where the draft
 * coerces to null and approving would error rather than open a blank form.
 */
export function CreateSkillConfirmation({
  params,
}: ConfirmationContentProps<CreateSkillInput>) {
  const draft = coerceToSkillDraft(params);

  if (draft === null) {
    return (
      <ConfirmationPanel label="Create skill preview">
        <ConfirmationLead>
          There&rsquo;s nothing to open the editor with yet — the draft needs at
          least a name, a description, or a procedure.
        </ConfirmationLead>
      </ConfirmationPanel>
    );
  }

  const { name, description, body, accessibility, tags = [] } = draft;

  return (
    <ConfirmationPanel label="Create skill preview">
      <ConfirmationLead>
        Open the Skills editor with this draft?
      </ConfirmationLead>
      <ConfirmationCard
        title={name ?? "(unnamed)"}
        badge={accessibility && VISIBILITY_LABELS[accessibility]}
      >
        <ConfirmationFields>
          {description && (
            <ConfirmationField label="When to use">
              {description}
            </ConfirmationField>
          )}
          {body && (
            <ConfirmationField label="Procedure">
              <div className={styles.body}>
                <RobotoMarkdown content={body} variant="compact" />
              </div>
            </ConfirmationField>
          )}
          {tags.length > 0 && (
            <ConfirmationField label="Tags">
              <ConfirmationTags tags={tags} />
            </ConfirmationField>
          )}
        </ConfirmationFields>
      </ConfirmationCard>
      <ConfirmationNote>
        Approving opens the Skills editor pre-filled with this draft — the skill
        isn&rsquo;t created until you review it and click Save there.
      </ConfirmationNote>
    </ConfirmationPanel>
  );
}
